import React, { useState, useEffect } from 'react';

const CpcTrafficClientsHistory = ({ recordId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [recordId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/cpc-traffic-clients/${recordId}/history`);
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      alert('Error al cargar el historial del registro');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'created':
        return '✨';
      case 'updated':
        return '📝';
      case 'status_changed':
        return '🔄';
      case 'comment_added':
        return '💬';
      case 'action_taken':
        return '⚡';
      case 'issue_resolved':
        return '✅';
      case 'escalated':
        return '⬆️';
      case 'follow_up':
        return '📞';
      default:
        return '📋';
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'created':
        return 'text-green-600 bg-green-50';
      case 'updated':
        return 'text-blue-600 bg-blue-50';
      case 'status_changed':
        return 'text-purple-600 bg-purple-50';
      case 'comment_added':
        return 'text-gray-600 bg-gray-50';
      case 'action_taken':
        return 'text-orange-600 bg-orange-50';
      case 'issue_resolved':
        return 'text-green-600 bg-green-50';
      case 'escalated':
        return 'text-red-600 bg-red-50';
      case 'follow_up':
        return 'text-indigo-600 bg-indigo-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha no disponible';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const getActionTypeText = (actionType) => {
    switch (actionType) {
      case 'created':
        return 'Registro Creado';
      case 'updated':
        return 'Registro Actualizado';
      case 'status_changed':
        return 'Estado Cambiado';
      case 'comment_added':
        return 'Comentario Agregado';
      case 'action_taken':
        return 'Acción Realizada';
      case 'issue_resolved':
        return 'Problema Resuelto';
      case 'escalated':
        return 'Escalado';
      case 'follow_up':
        return 'Seguimiento';
      default:
        return actionType;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">
              📋 Historial - Aumento CPC y Disminución Tráfico
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-lg text-gray-600">Cargando historial...</span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Sin historial disponible
              </h3>
              <p className="text-gray-600">
                No hay entradas de historial para este registro aún.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry, index) => (
                <div key={entry.id} className="relative">
                  {/* Línea de tiempo */}
                  {index < history.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-full bg-gray-200"></div>
                  )}
                  
                  <div className="flex items-start space-x-4">
                    {/* Icono */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-lg ${getActionColor(entry.action_type)}`}>
                      {getActionIcon(entry.action_type)}
                    </div>
                    
                    {/* Contenido */}
                    <div className="flex-1 min-w-0">
                      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="text-sm font-semibold text-gray-900">
                            {getActionTypeText(entry.action_type)}
                          </h4>
                          <span className="text-xs text-gray-500">
                            {formatDate(entry.created_at)}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-2">
                          {entry.description}
                        </p>
                        
                        {entry.details && (
                          <div className="bg-gray-50 rounded p-3 mt-2">
                            <p className="text-xs text-gray-600">
                              <strong>Detalles:</strong> {entry.details}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-100">
                          <span className="text-xs text-gray-500">
                            Por: {entry.user_name}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${getActionColor(entry.action_type)}`}>
                            {getActionTypeText(entry.action_type)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">
              {history.length} entrada{history.length !== 1 ? 's' : ''} en el historial
            </span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CpcTrafficClientsHistory;
